import { action, Action, thunk, Thunk } from 'easy-peasy'
import client from 'utils/locationService'
import debounce from 'lodash/debounce'

import { BankrateLocation } from '@bankrate/mortgage-types'
import { rollbar } from 'utils/rollbar'

export interface LocationModel {
  location: BankrateLocation

  setLocation: Action<LocationModel, BankrateLocation>

  update: Thunk<LocationModel, string>
}

enum ErrorTypeEnum {
  unknnown = 0,
  connectivity,
  serverError = 2,
  clientError,
  badZip,
  invalidZip,
}

const debounceUpdate = debounce(
  async (location: BankrateLocation, zipCode: string, actions: any, getStoreActions: any, resolve: any) => {
    // const averagePurchasePrice = 489450
    // const averagePurchaseLtV = 0.8
    const averagePurchaseLoanamount = 319560

    // const averageRefiPropertValue = 489450
    // const averageRefiLoanBalance = 0.6
    const averageRefiLoanAmounts = 293670

    const invalidLocation = {
      zipcode: zipCode,
      marketId: 0,
      stateId: 0,
      jumboAmount: 0,
      city: '',
      state: '',
      defaultLoanAmounts: {
        purchase: averagePurchaseLoanamount,
        refinance: averageRefiLoanAmounts,
      },
      type: 'Invalid',
    }

    try {
      location = await getLocation(zipCode)

      actions.setLocation(location || invalidLocation)

      if (location?.state) {
        updateGlobalLocationData(location)
      }
    } catch (err: any) {
      let errorType = ErrorTypeEnum.unknnown

      if (err.response === undefined || err.response.status === undefined || err.response.status === 0) {
        errorType = ErrorTypeEnum.connectivity
      } else {
        if (err.response.status >= 400 && err.response.status <= 499) {
          errorType = ErrorTypeEnum.clientError
          if (err.response?.status === 400) {
            errorType = ErrorTypeEnum.badZip
          }
          if (err.response?.status === 404) {
            errorType = ErrorTypeEnum.invalidZip
          }
        }
        if (err.response.status >= 500) {
          errorType = ErrorTypeEnum.serverError
        }
      }

      if (
        errorType === ErrorTypeEnum.connectivity ||
        errorType === ErrorTypeEnum.serverError ||
        errorType === ErrorTypeEnum.unknnown
      ) {
        rollbar.error(`check zip location failed`, { error: `${ErrorTypeEnum[errorType]}` })

        invalidLocation.type = 'zipcode'
      }

      if (errorType === ErrorTypeEnum.invalidZip) {
        getStoreActions()['datadog'].increment({
          name: 'location.error',
          value: 1,
          tags: [errorType, `invalidZip:${zipCode}`],
        })
      }

      actions.setLocation(invalidLocation)
    }

    resolve()
  },
  1000
)

const location: LocationModel = {
  location: initialLocation(),

  setLocation: action((state, location: BankrateLocation) => {
    state.location = location
  }),

  update: thunk(async (actions, zipCode, { getState, getStoreActions }) => {
    let location = getState().location

    // cheaper than memo
    if (location.zipcode === zipCode) {
      return
    }

    return new Promise(resolve => debounceUpdate(location, zipCode, actions, getStoreActions, resolve))
  }),
}

export function initialLocation() {
  // fastly should set this
  if (globalThis.locationData) return globalThis.locationData

  // geolocation miss or we're testing; a miss has never been recorded
  return {
    zipcode: '33410',
    marketId: 98,
    stateId: 10,
    jumboAmount: 510400,
    city: 'West Palm Beach',
    state: 'FL',
    defaultLoanAmounts: {
      refinance: 360000,
      purchase: 360000,
    },
    type: 'zipcode',
  }
}

async function getLocation(zipCode: string) {
  const { data } = await client.get(`/${zipCode}`)
  return data
}

function updateGlobalLocationData(locationData: BankrateLocation) {
  try {
    if (typeof globalThis?.locationData === 'object') {
      Object.assign(globalThis.locationData, locationData)
    }
  } catch (err) {
    rollbar.error('Failed to merge location data', { err })
  }
}

export default location
